import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSocketFallback } from "hooks/useSocketFallback";
import { useSnackbar } from "hooks/useSnackbar";
import miroService from "api/miro/miro.service";
import { IMiroCanvasResult, IMiroExportResult, IMiroImportedAsset, IMiroImportResult, IMiroJob, IMiroJobProgress, IMiroJobResult, IMiroJobSummary } from "api/miro/miro.type";
import MiroJobsTray from "components/MiroJobsTray";
import { PATHS } from "router/config/paths";
import { getMiroErrorCode, isPaywallError, miroBackgroundToastId, miroMessageKey } from "utils/miro";
import { moodboardUserResults } from "utils/miroMoodboard";
import { boardMoodboardErrorKey, boardMoodboardOutcome, boardMoodboardStartedKey } from "utils/miroBoardMoodboard";
import { CANVAS_IMPORTED_EVENT } from "utils/miroEvents";
import { canvasImportErrorKey, summarizeCanvasImport, summarizeCanvasResults } from "utils/miroCanvas";
import { canvasImportLines, canvasImportProducedSomething, canvasImportText, exportCommentLines, exportedNoteCount, exportNoteLines, joinLines } from "utils/miroCanvasMessages";
import { markJobNotified, wasJobNotified } from "utils/miroNotified";
import { trackEvent } from "utils/analytics";
import { EVENTS } from "utils/analytics.events";

export type MiroJobKind = "export" | "import" | "moodboard" | "canvas" | "canvas_import" | "board_moodboard";
export type MiroJobPhase = "running" | "succeeded" | "failed";

export interface MiroJobEntry {
  jobId: string;
  kind: MiroJobKind;
  /** Items the BE accepted (the 202's `total`); the running count arrives as `progress`. */
  total: number;
  phase: MiroJobPhase;
  result?: IMiroJobResult;
  /** `failed` only: the BE's text, or an error code (`miro.job_not_found`, `http_4xx`, `timeout`) when the FE gave up. */
  error?: string | null;
  /** Items done so far while it runs (the BE moves it in steps); `null` until the BE reports one → indeterminate. */
  progress?: IMiroJobProgress | null;
  /** The user closed the dialog while it ran: the provider reports the outcome itself. */
  backgrounded: boolean;
  /** Analytics label / retry flag the host attached. */
  meta?: { surface?: string; retry?: boolean };
  /** The target board (for the tray's "Open board" link); a settled job's `result.board` is preferred. */
  board?: { name?: string | null; view_link?: string | null } | null;
  /** ms epoch the job settled — for ordering the tray's finished list. */
  finishedAt?: number;
  /** The provider can start this same job again (`retryJob`) — set when the host handed over a `restart`. */
  retryable?: boolean;
}

/** The BE's job-failure code for a job the engine never started within 5 minutes: worth offering a retry, not an apology. */
export const MIRO_JOB_NOT_STARTED = "miro.job_not_started";
export const isJobNotStarted = (entry: Pick<MiroJobEntry, "phase" | "error">): boolean => entry.phase === "failed" && entry.error === MIRO_JOB_NOT_STARTED;

interface TrackOptions {
  meta?: MiroJobEntry["meta"];
  board?: MiroJobEntry["board"];
  /** Runs when a BACKGROUNDED job settles (a foreground job's host reads the entry instead). */
  onBackgroundSettled?: (entry: MiroJobEntry) => void;
  /**
   * Starts the SAME request again and resolves to the new 202 (`null` = nothing started). Self-contained on purpose — it
   * must work after the dialog that built it is gone — so it closes over the already-built request, never over component state.
   */
  restart?: () => Promise<{ job_id: string; total: number } | null | undefined>;
}

interface MiroJobsContextValue {
  jobs: Record<string, MiroJobEntry>;
  /** Start watching a job the BE just accepted. */
  track: (job: { jobId: string; kind: MiroJobKind; total: number }, options?: TrackOptions) => void;
  /** The dialog is closing while the job runs — report the outcome with a snackbar when it ends. */
  background: (jobId: string) => void;
  /** Record a running job's latest progress (ignored once it settled). */
  reportProgress: (jobId: string, progress: IMiroJobProgress | null) => void;
  /** Drop a settled entry once its host has consumed it. */
  forget: (jobId: string) => void;
  /** Start a failed job's request again (its `restart`); the new job runs in the background and the old finished row goes away. */
  retryJob: (jobId: string) => Promise<void>;
  /** Backgrounded jobs that finished, newest first — the tray lists them until dismissed. */
  finished: MiroJobEntry[];
  dismissFinished: (jobId: string) => void;
  /** After a reload: resume watching running jobs and announce ones that finished while the page was closed. */
  resume: () => Promise<void>;
  /** Hand the assets a finished import created to My Assets' edit step (navigates there). */
  requestEditAssets: (assets: IMiroImportedAsset[]) => void;
  /** Assets waiting for My Assets to open its edit step; `consumeEditAssets` takes them. */
  pendingEditAssets: IMiroImportedAsset[] | null;
  consumeEditAssets: () => void;
}

const noop = () => undefined;
const MiroJobsContext = createContext<MiroJobsContextValue>({
  jobs: {},
  track: noop,
  background: noop,
  reportProgress: noop,
  forget: noop,
  retryJob: async () => undefined,
  finished: [],
  dismissFinished: noop,
  resume: async () => undefined,
  requestEditAssets: noop,
  pendingEditAssets: null,
  consumeEditAssets: noop,
});

export const useMiroJobs = () => useContext(MiroJobsContext);

// `[LONG_JOB_SOCKET_FALLBACK]` — the BE publishes no WS frame for a Miro job, so the monitor is the poll:
// first check after 1 s, then every 2 s (the cadence the BE asks for). Hoisted to module scope: an inline
// `designTypes` / `customStatusCheck` is new every render and would restart the monitor.
const MIRO_JOB_DESIGN_TYPES = ["miro_job"] as const;
const MIRO_JOB_FIRST_POLL_MS = 1000;
const MIRO_JOB_POLL_MS = 2000;
const MIRO_JOB_MAX_POLL_MS = 30 * 60 * 1000;
// 4xx answers worth retrying: request timeout, too early, rate limited.
const TRANSIENT_4XX = new Set([408, 425, 429]);
// What may be sent as an analytics code — never free text from the BE (`job.error` is a sentence).
const ERROR_CODE = /^(miro\.[a-z_]+|timeout|http_\d{3})$/;

const checkMiroJob = async (jobId: string): Promise<{ status: number; data?: IMiroJob; progress?: IMiroJobProgress | null }> => {
  try {
    const res = await miroService.getJob(jobId);
    const job = res?.responseData;
    // A resolved response is not a success — a handled 401/403/503 resolves with `_handled`: still pending.
    if (res?._handled || !job) return { status: 0 };
    if (job.status === "succeeded") return { status: 1, data: job };
    if (job.status === "failed" || job.status === "cancelled") return { status: 99, data: job };

    return { status: 0, progress: job.progress ?? null };
  } catch (err) {
    const http = (err as { response?: { status?: number } })?.response?.status;
    const code = getMiroErrorCode(err);
    // The job is gone (or never was ours), or the request itself is refused (401/402/403/404/422…): polling again
    // would get the same answer for 30 minutes. Stop and surface the error.
    if (code === "miro.job_not_found" || (http !== undefined && http >= 400 && http < 500 && !TRANSIENT_4XX.has(http))) {
      return { status: 99, data: { job_id: jobId, status: "failed", error: code ?? `http_${http}` } };
    }
    // Network errors, 5xx and the retryable 4xx are transient — the hook keeps polling.
    throw err;
  }
};

type Settled = { phase: "succeeded"; job: IMiroJob } | { phase: "failed"; job?: IMiroJob; error: string };

/** One monitor per job: a `useSocketFallback` watches exactly ONE id. */
const MiroJobMonitor = ({
  jobId,
  onSettled,
  onProgress,
  onPoll,
}: {
  jobId: string;
  onSettled: (jobId: string, settled: Settled) => void;
  onProgress: (jobId: string, progress: IMiroJobProgress | null) => void;
  onPoll: (jobId: string) => void;
}) => {
  // Stable for the life of this monitor (a changing `customStatusCheck` restarts it): reads the latest callback through a ref.
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  const onPollRef = useRef(onPoll);
  onPollRef.current = onPoll;
  const check = useCallback(async (id: string) => {
    onPollRef.current(id);
    const result = await checkMiroJob(id);
    if (result.status === 0) onProgressRef.current(id, result.progress ?? null);

    return result;
  }, []);

  const { startMonitoring } = useSocketFallback({
    designTypes: MIRO_JOB_DESIGN_TYPES,
    isActive: true,
    waitDuration: MIRO_JOB_FIRST_POLL_MS,
    pollingInterval: MIRO_JOB_POLL_MS,
    maxPollingDuration: MIRO_JOB_MAX_POLL_MS,
    customStatusCheck: check,
    onSuccess: (job: IMiroJob) => onSettled(jobId, { phase: "succeeded", job }),
    onError: (job: IMiroJob) => onSettled(jobId, { phase: "failed", job, error: job?.error || job?.result?.error_code || "failed" }),
    onTimeout: () => onSettled(jobId, { phase: "failed", error: "timeout" }),
  });

  useEffect(() => {
    startMonitoring(jobId);
  }, [jobId, startMonitoring]);

  return null;
};

// A `/design-assets/<tab>` page (Fabrics, Prints, Others…) — these carry the filters bar that opens the edit step.
// `fabric-trainings` lives under the same prefix but is not an assets tab.
const ASSET_TAB_PATH = /^\/design-assets\/(?!fabric-trainings(\/|$))[^/]+\/?$/;

export const isAssetsTab = (pathname: string): boolean => ASSET_TAB_PATH.test(pathname);

// The BE's job `kind` vocabulary → ours.
const JOB_KIND_FROM_BE: Record<IMiroJobSummary["kind"], MiroJobKind> = {
  export: "export",
  import: "import",
  export_moodboard: "moodboard",
  moodboard: "moodboard",
  export_canvas: "canvas",
  import_canvas: "canvas_import",
  moodboard_from_board: "board_moodboard",
};

export { CANVAS_IMPORTED_EVENT };

const RESUME_WINDOW_MS = 30 * 60 * 1000;
const MAX_FINISHED = 10;
// Polling is watched from outside: a monitor is expected to ask every ~2 s, and one that has been silent this long
// (longer than a hung request's 15 s ceiling) is rebuilt. Whatever silenced it — a hook that lost its state, a
// throttled timer, a remount — the job must never be left unwatched while the user believes it is running.
const WATCHDOG_TICK_MS = 5000;
const WATCHDOG_STALE_MS = 20000;

/** The assets a finished import CREATED (never duplicates) — what the edit step opens. */
export const createdAssets = (entry: Pick<MiroJobEntry, "kind" | "result">): IMiroImportedAsset[] =>
  entry.kind === "import" ? ((entry.result?.results ?? []) as IMiroImportResult[]).flatMap((r) => (r.status === "imported" && r.asset ? [r.asset] : [])) : [];

const countBy = <T extends { status: string }>(list: T[], status: string) => list.filter((r) => r.status === status).length;

export const MiroJobsProvider = ({ children }: { children: React.ReactNode }) => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, showInfo, dismiss } = useSnackbar();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [finished, setFinished] = useState<MiroJobEntry[]>([]);
  const [pendingEditAssets, setPendingEditAssets] = useState<IMiroImportedAsset[] | null>(null);
  const [jobs, setJobs] = useState<Record<string, MiroJobEntry>>({});
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  // When each running job's monitor last asked the BE (or was started) — what the watchdog reads — and how many times
  // its monitor has been rebuilt (part of the monitor's key).
  const lastPollAtRef = useRef<Record<string, number>>({});
  const [monitorEpochs, setMonitorEpochs] = useState<Record<string, number>>({});
  const markPolled = useCallback((jobId: string) => {
    lastPollAtRef.current[jobId] = Date.now();
  }, []);
  const finishedRef = useRef(finished);
  finishedRef.current = finished;
  const handlersRef = useRef<Record<string, TrackOptions["onBackgroundSettled"]>>({});
  // What `retryJob` needs, kept beyond `forget` (a retry is offered after the job — and often its dialog — is gone).
  const restartRef = useRef<Record<string, { kind: MiroJobKind; options: TrackOptions }>>({});

  const track = useCallback<MiroJobsContextValue["track"]>((job, options) => {
    if (options?.onBackgroundSettled) handlersRef.current[job.jobId] = options.onBackgroundSettled;
    if (options?.restart) restartRef.current[job.jobId] = { kind: job.kind, options };
    lastPollAtRef.current[job.jobId] = Date.now();
    setJobs((prev) => ({ ...prev, [job.jobId]: { ...job, phase: "running", backgrounded: false, meta: options?.meta, board: options?.board, retryable: Boolean(options?.restart) } }));
  }, []);

  const reportProgress = useCallback<MiroJobsContextValue["reportProgress"]>((jobId, progress) => {
    setJobs((prev) => {
      const current = prev[jobId];
      if (!current || current.phase !== "running") return prev;
      if (current.progress?.done === progress?.done && current.progress?.total === progress?.total) return prev; // no re-render for an unchanged poll

      return { ...prev, [jobId]: { ...current, progress } };
    });
  }, []);

  const background = useCallback((jobId: string) => {
    setJobs((prev) => (prev[jobId] ? { ...prev, [jobId]: { ...prev[jobId], backgrounded: true } } : prev));
  }, []);

  const forget = useCallback((jobId: string) => {
    delete handlersRef.current[jobId];
    setJobs((prev) => {
      if (!prev[jobId]) return prev;
      const next = { ...prev };
      delete next[jobId];

      return next;
    });
  }, []);

  const requestEditAssets = useCallback(
    (assets: IMiroImportedAsset[]) => {
      if (assets.length === 0) return;
      setPendingEditAssets(assets);
      // My Assets owns the edit step (it needs that page's asset context): its filters bar opens the editor. Already on
      // one of its tabs (`/design-assets/<tab>`, e.g. Others)? Stay there — `/design-assets` alone redirects to Fabrics.
      if (!isAssetsTab(pathname)) navigate(PATHS.DESIGN_ASSETS);
    },
    [navigate, pathname]
  );
  const consumeEditAssets = useCallback(() => setPendingEditAssets(null), []);
  const dismissFinished = useCallback((jobId: string) => {
    delete restartRef.current[jobId];
    setFinished((prev) => prev.filter((e) => e.jobId !== jobId));
  }, []);

  const retryJob = useCallback(
    async (jobId: string) => {
      const saved = restartRef.current[jobId];
      if (!saved?.options.restart) return;

      try {
        const accepted = await saved.options.restart();
        if (!accepted) return;

        dismissFinished(jobId);
        track({ jobId: accepted.job_id, kind: saved.kind, total: accepted.total }, saved.options);
        // A retry is started from the tray: nothing is watching a dialog for it.
        background(accepted.job_id);
        trackEvent(EVENTS.MIRO.RETRY, { kind: saved.kind });
      } catch (err) {
        // 402 = permission paywall, already shown by HttpClient. Anything else: say so, and keep the retry available.
        if (isPaywallError(err)) return;
        showError(t(miroMessageKey(getMiroErrorCode(err), "errors", i18n.exists) ?? "miro:errors.generic"));
      }
    },
    [dismissFinished, track, background, showError, t, i18n]
  );

  // One place words a job's outcome, foreground or background — the dialog only renders the per-item view.
  const announce = useCallback(
    (entry: MiroJobEntry) => {
      const openBoard = (link: string | null | undefined) => (link ? { action: { label: t("miro:send.open_board"), onClick: () => window.open(link, "_blank", "noopener,noreferrer") } } : undefined);
      const reason = (code?: string) => (code ? miroMessageKey(code, "errors", i18n.exists) : null);

      if (entry.phase === "failed") {
        trackEvent(entry.kind === "import" ? EVENTS.MIRO.IMPORT_ERROR : EVENTS.MIRO.SEND_ERROR, {
          surface: entry.meta?.surface,
          code: entry.error && ERROR_CODE.test(entry.error) ? entry.error : "job_failed",
        });
        // A job the engine never started: offer to start it again rather than apologise.
        if (isJobNotStarted(entry)) {
          showError(t("miro:job.not_started"), entry.retryable ? { action: { label: t("miro:job.retry"), onClick: () => void retryJob(entry.jobId) } } : undefined);

          return;
        }
        showError(t(entry.error === "timeout" ? "miro:job.timeout" : "miro:job.failed"));

        return;
      }

      const result = entry.result;
      const code = result?.error_code;
      const list = result?.results ?? [];

      if (entry.kind === "import") {
        const results = list as IMiroImportResult[];
        const imported = countBy(results, "imported");
        const duplicate = countBy(results, "duplicate");
        const failed = countBy(results, "failed");
        trackEvent(EVENTS.MIRO.IMPORT_COMPLETE, { imported, duplicate, failed });
        if (imported > 0) {
          // A foreground import opens its edit step in the dialog; one that finished in the background offers it here.
          const created = createdAssets(entry);
          const editAction = entry.backgrounded && created.length > 0 ? { action: { label: t("miro:import.edit_assets"), onClick: () => requestEditAssets(created) } } : undefined;
          showSuccess(duplicate > 0 && failed === 0 ? t("miro:import.success_with_duplicates", { imported, duplicate }) : t("miro:import.success", { count: imported }), editAction);
          if (failed > 0) showError(t("miro:import.skipped_note", { count: duplicate + failed }));
        } else if (duplicate > 0 && failed === 0) {
          showInfo(t("miro:import.all_duplicates"));
        } else {
          showError(t(reason(code) ?? "miro:import.all_failed"));
        }

        return;
      }

      if (entry.kind === "canvas_import") {
        const { imported, duplicate, failed, skipped } = summarizeCanvasImport(result);
        const canvas = result?.canvas ?? null;
        trackEvent(EVENTS.MIRO.CANVAS_IMPORT_COMPLETE, { imported, duplicate, failed, skipped });
        // A canvas that exists is in the user's list now, whatever the outcome: tell the Canvas Projects page (if it is open) to reload it.
        if (canvas) window.dispatchEvent(new CustomEvent(CANVAS_IMPORTED_EVENT, { detail: { canvasId: canvas.id } }));
        // A sticky-only board makes a canvas too (no images; comments or notes): the canvas, not the image count, is the success.
        if (canvas && canvasImportProducedSomething(result)) {
          showSuccess(canvasImportText(t, i18n.exists, result), {
            action: { label: t("miro:canvas_import.open_canvas"), onClick: () => navigate(`${PATHS.DESIGN_STUDIO}?id=${canvas.id}`) },
          });
        } else {
          // Sticky notes / comments left out or failed are said too, never hidden behind "nothing imported".
          // A block failing for the job's own reason would say it twice: the job's message already does.
          const own = <T extends { error_code?: string | null }>(block: T | null | undefined) => (block && code && block.error_code === code ? { ...block, error_code: undefined } : block);
          const lines = canvasImportLines(t, i18n.exists, result && { ...result, notes: own(result.notes), comments: own(result.comments) });
          const warnings = [...lines.notes, ...lines.warnings].map((line) => ` ${line}`).join("");
          if (code) showError(`${t(canvasImportErrorKey(code, i18n.exists) ?? "miro:canvas_import.all_failed")}${warnings}`);
          else if (warnings) showError(`${t("miro:canvas_import.nothing_imported")}${warnings}`);
          else showInfo(t("miro:canvas_import.nothing_imported"));
        }

        return;
      }

      if (entry.kind === "board_moodboard") {
        const { uploadId, split, images, included } = boardMoodboardOutcome(result);
        trackEvent(EVENTS.MIRO.BOARD_MOODBOARD_COMPLETE, { images, included });
        if (uploadId) {
          // The folder upload reads the deck for a few minutes; My Moodboards shows it while it does.
          showSuccess(t(boardMoodboardStartedKey(split)), { action: { label: t("miro:board_moodboard.open_moodboards"), onClick: () => navigate(PATHS.MOODBOARDS_MY) } });
        } else if (code) {
          showError(t(boardMoodboardErrorKey(code, i18n.exists) ?? "miro:board_moodboard.failed"));
        } else {
          showInfo(t("miro:board_moodboard.nothing_to_import"));
        }

        return;
      }

      if (entry.kind === "canvas") {
        // Main images only; versions and layout labels are not "N exported" (see `summarizeCanvasResults`).
        const { created, skipped, failed } = summarizeCanvasResults(list as IMiroCanvasResult[]);
        trackEvent(EVENTS.MIRO.CANVAS_COMPLETE, { created, skipped, failed });
        // `skipped` is neutral (nothing Miro can show): never a failure.
        // REF-7145: the canvas's sticky notes go too; a notes-only export (no image) is still a success.
        const commentLines = joinLines(exportCommentLines(t, i18n.exists, result), exportNoteLines(t, i18n.exists, result));
        const commentsLine = [commentLines.done, ...commentLines.notes, ...commentLines.warnings]
          .filter(Boolean)
          .map((line) => ` ${line}`)
          .join("");
        if (created > 0 && failed === 0) showSuccess(`${t("miro:canvas.success", { count: created })}${commentsLine}`, openBoard(result?.board?.view_link ?? entry.board?.view_link));
        else if (created > 0) showSuccess(`${t("miro:canvas.partial", { created, failed })}${commentsLine}`, openBoard(result?.board?.view_link ?? entry.board?.view_link));
        else if (exportedNoteCount(result) > 0) {
          // images that failed are still named next to the notes that went across
          const failedLine = failed > 0 ? ` ${t("miro:canvas.failed", { count: failed })}` : "";
          showSuccess(`${commentsLine.trim()}${failedLine}`, openBoard(result?.board?.view_link ?? entry.board?.view_link));
        } else if (failed === 0 && commentLines.warnings.length > 0) showError(commentsLine.trim());
        else if (failed === 0) showInfo(t("miro:canvas.nothing_exported"));
        else showError(`${t(reason(code) ?? "miro:canvas.all_failed")}${commentsLine}`);

        return;
      }

      // A moodboard's title and section labels are layout, not the user's items: count only what they sent.
      const results = (entry.kind === "moodboard" ? moodboardUserResults(list as IMiroExportResult[]) : list) as IMiroExportResult[];
      const created = countBy(results, "created");
      const failed = results.length - created;

      if (entry.kind === "moodboard") {
        trackEvent(EVENTS.MIRO.MOODBOARD_COMPLETE, { created, failed });
        if (created > 0 && failed === 0) showSuccess(t("miro:moodboard.success"), openBoard(result?.board?.view_link));
        else if (created > 0) showSuccess(t("miro:moodboard.partial", { created, failed }), openBoard(result?.board?.view_link));
        else showError(t(reason(code) ?? "miro:moodboard.all_failed"));

        return;
      }

      trackEvent(EVENTS.MIRO.SEND_COMPLETE, { surface: entry.meta?.surface, created, failed, retry: Boolean(entry.meta?.retry) });
      if (created > 0) showSuccess(t("miro:send.success", { count: created }), openBoard(result?.board?.view_link));
      else showError(t(reason(code) ?? "miro:send.all_failed"));
    },
    [t, i18n, showSuccess, showError, showInfo, requestEditAssets, retryJob]
  );

  const handleSettled = useCallback(
    (jobId: string, settled: Settled) => {
      const current = jobsRef.current[jobId];
      if (!current || current.phase !== "running") return;

      const entry: MiroJobEntry = {
        ...current,
        phase: settled.phase,
        result: settled.phase === "succeeded" ? settled.job.result ?? undefined : undefined,
        error: settled.phase === "failed" ? settled.error : undefined,
      };
      setJobs((prev) => (prev[jobId] ? { ...prev, [jobId]: entry } : prev));
      delete lastPollAtRef.current[jobId];
      // The "still working" toast of a backgrounded job gives way to the outcome.
      dismiss(miroBackgroundToastId(jobId));
      announce(entry);
      markJobNotified(jobId);

      if (entry.backgrounded) {
        handlersRef.current[jobId]?.(entry);
        setFinished((prev) => [{ ...entry, finishedAt: Date.now() }, ...prev.filter((e) => e.jobId !== jobId)].slice(0, MAX_FINISHED));
        forget(jobId);
      }
    },
    [announce, forget, dismiss]
  );

  // A job whose dialog closed AFTER it had already settled (the user hit close in the same tick) has no host left to forget it.
  useEffect(() => {
    Object.values(jobs).forEach((entry) => {
      if (entry.backgrounded && entry.phase !== "running") forget(entry.jobId);
    });
  }, [jobs, forget]);

  // After a reload the in-memory jobs are gone, but the BE still has them. Running ones are adopted (and polled
  // again); ones that finished in the last ~30 min and that this browser has not announced yet are announced once.
  const resume = useCallback(async () => {
    try {
      // `since_minutes` also returns the jobs that finished while the page was closed (the BE default is only the running ones).
      const res = await miroService.listJobs({ active: true, since_minutes: RESUME_WINDOW_MS / 60000 });
      const data = res?.responseData;
      if (res?._handled || !data) return;

      const list: IMiroJobSummary[] = Array.isArray(data) ? data : data.items ?? [];
      for (const summary of list) {
        const id = summary.job_id;
        if (!id || jobsRef.current[id] || finishedRef.current.some((e) => e.jobId === id)) continue;

        const base = {
          jobId: id,
          kind: JOB_KIND_FROM_BE[summary.kind] ?? "export",
          total: summary.progress?.total ?? 0,
          backgrounded: true,
          board: summary.board ?? null,
        };
        if (summary.status === "queued" || summary.status === "running") {
          lastPollAtRef.current[id] = Date.now();
          setJobs((prev) => (prev[id] ? prev : { ...prev, [id]: { ...base, phase: "running", progress: summary.progress ?? null } }));
          continue;
        }

        const finishedAt = summary.finished_at ? Date.parse(summary.finished_at) : NaN;
        if (Number.isNaN(finishedAt) || Date.now() - finishedAt > RESUME_WINDOW_MS || wasJobNotified(id)) continue;

        const job = (await miroService.getJob(id))?.responseData;
        if (!job) continue;
        const entry: MiroJobEntry = {
          ...base,
          phase: job.status === "succeeded" ? "succeeded" : "failed",
          result: job.status === "succeeded" ? job.result ?? undefined : undefined,
          error: job.status === "succeeded" ? undefined : job.error || "failed",
          finishedAt,
        };
        announce(entry);
        markJobNotified(id);
        setFinished((prev) => [entry, ...prev.filter((e) => e.jobId !== id)].slice(0, MAX_FINISHED));
      }
    } catch {
      // Not connected / no access / offline: nothing to resume — and nothing worth telling the user at app start.
    }
  }, [announce]);

  const runningIds = Object.values(jobs)
    .filter((j) => j.phase === "running")
    .map((j) => j.jobId)
    .join(",");
  useEffect(() => {
    if (!runningIds) return undefined;

    const timer = window.setInterval(() => {
      const now = Date.now();
      const stale = runningIds.split(",").filter((id) => now - (lastPollAtRef.current[id] ?? now) > WATCHDOG_STALE_MS);
      if (stale.length === 0) return;

      stale.forEach((id) => {
        lastPollAtRef.current[id] = now;
      });
      setMonitorEpochs((prev) => ({ ...prev, ...Object.fromEntries(stale.map((id) => [id, (prev[id] ?? 0) + 1])) }));
    }, WATCHDOG_TICK_MS);

    return () => window.clearInterval(timer);
  }, [runningIds]);

  const value = useMemo(
    () => ({ jobs, track, background, reportProgress, forget, retryJob, finished, dismissFinished, resume, requestEditAssets, pendingEditAssets, consumeEditAssets }),
    [jobs, track, background, reportProgress, forget, retryJob, finished, dismissFinished, resume, requestEditAssets, pendingEditAssets, consumeEditAssets]
  );
  const running = Object.values(jobs).filter((j) => j.phase === "running");

  return (
    <MiroJobsContext.Provider value={value}>
      {children}
      <MiroJobsTray />
      {running.map((job) => (
        <MiroJobMonitor key={`${job.jobId}:${monitorEpochs[job.jobId] ?? 0}`} jobId={job.jobId} onSettled={handleSettled} onProgress={reportProgress} onPoll={markPolled} />
      ))}
    </MiroJobsContext.Provider>
  );
};
